  // PaymentRestructureForm.jsx
  import { useEffect, useMemo, useRef, useState } from "react";
  import { createPortal } from "react-dom";
  import { X, AlertTriangle, Plus, Trash2 } from "lucide-react";
  import { toast } from "react-toastify";
  import { BASE_URL } from "shared/lib/config";

  /* =========================================================
  * Auth + fetch helpers
  * ========================================================= */
  const getStore = () => {
    if (sessionStorage.getItem("idToken")) return sessionStorage;
    if (localStorage.getItem("idToken")) return localStorage;
    return localStorage;
  };
  const getToken = () => getStore().getItem("idToken");

  async function fetchJson(url, opts = {}) {
    const token = getToken();
    const headers = {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const res = await fetch(url, { ...opts, headers });
    const contentType = res.headers.get("content-type") || "";

    let data = null;
    if (contentType.includes("application/json")) data = await res.json().catch(() => null);
    else data = await res.text().catch(() => null);

    if (!res.ok) {
      const msg = data?.message || (typeof data === "string" && data) || `Request failed (${res.status})`;
      const err = new Error(msg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }
function padOrTrimPlanToLen(plan, len) {
  const L = Math.max(1, Number(len || 1));
  let out = Array.isArray(plan) ? plan.slice(0, L) : [];
  while (out.length < L) out.push(0);

  // drift fix to 100 (4 decimals)
  out = out.map((x) => Math.max(0, Math.round(Number(x || 0) * 10000) / 10000));
  const sum = out.reduce((a, b) => a + b, 0);
  const diff = Math.round((100 - sum) * 10000) / 10000;
  out[L - 1] = Math.max(0, Math.round((out[L - 1] + diff) * 10000) / 10000);
  return out;
}

  /* =========================================================
  * Helpers
  * ========================================================= */
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(
      Math.max(0, Math.round(Number(amount || 0)))
    );

    // max 2 decimals, trims trailing zeros (e.g. 50 -> "50", 50.5 -> "50.5", 50.123 -> "50.12")
const fmt2 = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0";
  return (Math.round(x * 100) / 100).toFixed(2).replace(/\.?0+$/, "");
};

function enforceMin1Percents(pcts) {
  const arr = (pcts || []).map((x) => clampPercent(x));
  const n = arr.length || 1;
  if (n <= 1) return [100];

  // If user gives impossible input (e.g., many slots), we'll clamp then fix last.
  // We require each slot >= 1, so minimum sum is n.
  if (n > 100) return arr; // practically impossible in your UI anyway (max 12)

  // Force every slot >= 1
  for (let i = 0; i < n; i++) arr[i] = Math.max(1, arr[i]);

  // Fix sum to exactly 100 by adjusting last slot
  const sum = arr.reduce((s, x) => s + x, 0);
  const diff = 100 - sum;
  arr[n - 1] = Math.max(1, arr[n - 1] + diff);

  // If last got pushed below 1 by diff, re-balance from earlier slots
  if (arr[n - 1] < 1) {
    let need = 1 - arr[n - 1];
    arr[n - 1] = 1;

    for (let i = 0; i < n - 1 && need > 0; i++) {
      const canGive = Math.max(0, arr[i] - 1);
      const take = Math.min(canGive, need);
      arr[i] -= take;
      need -= take;
    }
  }

  // Final drift fix (keep last >=1)
  const sum2 = arr.reduce((s, x) => s + x, 0);
  if (sum2 !== 100) {
    arr[n - 1] = Math.max(1, arr[n - 1] + (100 - sum2));
  }

  return arr;
}

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  };

  function toISODateInput(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  function fromISODateInput(value) {
    const s = String(value || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    const d = new Date(`${s}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  }

  function clampNonNeg(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, x);
  }

  function clampPercent(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.min(100, x));
  }
  function enforceMin1Amounts(amounts, total) {
  const T = Math.max(0, Math.round(Number(total || 0)));
  const out = (amounts || []).map((x) => Math.max(0, Math.round(Number(x || 0))));
  const n = out.length || 1;

  if (T <= 0) return out;
  if (n === 1) return [Math.max(1, T)];
  if (T < n) return out; // impossible to give >=1 to each; leave as-is (will be caught by validation)

  // Make every row >= 1 by taking from last row
  for (let i = 0; i < n - 1; i++) {
    if (out[i] < 1) {
      out[i] = 1;
    }
  }

  // Rebalance last to keep sum == T
  const sumFirst = out.slice(0, n - 1).reduce((s, x) => s + x, 0);
  out[n - 1] = Math.max(1, T - sumFirst);

  return out;
}

  function normalizeMode(v) {
    const s = String(v || "").toLowerCase().trim();
    if (s === "plan") return "plan";
    if (s === "percentage") return "percentage";
    if (s === "amount") return "amount";
    if (s === "equal") return "equal";
    return "plan";
  }
 async function ensureCountTotal(saleId, totalCount) {
  return await fetchJson(`${BASE_URL}/api/invoice-tracker/sales/${saleId}/invoices/ensure-count`, {
    method: "PUT",
    body: JSON.stringify({ total_count: Number(totalCount) }),
  });
}


  function sumNums(arr) {
    return (arr || []).reduce((s, x) => s + (Number(x) || 0), 0);
  }

  // NOTE: despite name, this is a generic "equal split for integer targetSum" helper (works for 100 too)
  function buildEqualPercentagesForTarget(n, targetSum) {
    const N = Math.max(1, Math.floor(Number(n || 1)));
    const target = clampNonNeg(targetSum);
    if (N === 1) return [target];

    const base = Math.floor(target / N);
    const out = new Array(N).fill(base);
    const sum = out.reduce((a, b) => a + b, 0);
    out[N - 1] += target - sum;
    return out;
  }

  function coercePercentArray(arr, n) {
    const N = Math.max(1, Math.floor(Number(n || 1)));
    const out = Array.isArray(arr) ? arr.slice(0, N) : [];
    while (out.length < N) out.push(0);
    return out.map((x) => clampNonNeg(x));
  }

  function normalizeInvoiceStatus(v) {
    const s = String(v || "").trim().toLowerCase();
    if (!s) return "draft";
    return s;
  }
  function buildInstallmentPercentagesFromAmounts({ grandTotal, activeInvoices, previewUnpaidRows }) {
  const gt = Math.max(0, Math.round(Number(grandTotal || 0)));
  const planLen = Math.max(1, activeInvoices.length);

  // invoice_index -> amount
  const amountByIndex = new Map();

  // 1) locked/paid from DB invoices
  for (const inv of activeInvoices) {
    const idx = Number(inv?.invoice_index || 0);
    if (!idx) continue;

    const st = normalizeInvoiceStatus(inv?.status);
    if (st === "paid" || st === "paid overdue") {
      const amt = Math.max(0, Math.round(Number(inv?.totals?.grandTotal || 0)));
      amountByIndex.set(idx, amt);
    }
  }

  // 2) unpaid from preview rows the user sees
  for (const r of (previewUnpaidRows || [])) {
    const idx = Number(r?.invoice_index || 0);
    if (!idx) continue;

    if (!amountByIndex.has(idx)) {
      const amt = Math.max(0, Math.round(Number(r?.amount || 0)));
      amountByIndex.set(idx, amt);
    }
  }

  // 3) ensure every slot 1..planLen has an amount (fallback 0)
  const outAmounts = [];
  for (let i = 1; i <= planLen; i++) {
    outAmounts.push(amountByIndex.get(i) || 0);
  }

  // 4) convert to % of GRAND TOTAL and fix drift to exactly 100
  if (gt <= 0) {
    const base = Math.floor(100 / planLen);
    const out = new Array(planLen).fill(base);
    out[planLen - 1] += 100 - out.reduce((a, b) => a + b, 0);
    return out;
  }

  const pct4 = outAmounts.map((a) => Math.max(0, Math.round(((a / gt) * 100) * 10000) / 10000));
  const sum = pct4.reduce((a, b) => a + b, 0);
  const diff = Math.round((100 - sum) * 10000) / 10000;
  pct4[pct4.length - 1] = Math.max(0, Math.round((pct4[pct4.length - 1] + diff) * 10000) / 10000);

  return pct4;
}
  function amountsToPercents(amounts, total) {
  const T = Math.max(0, Math.round(Number(total || 0)));
  const n = amounts.length || 1;
  if (T <= 0) return buildEqualPercentagesForTarget(n, 100);

  const pct = amounts.map((a) => Math.max(0, (Number(a) || 0) / T * 100));
  // round 4 decimals
  const pct4 = pct.map((p) => Math.round(p * 10000) / 10000);

  const sum = pct4.reduce((s, x) => s + x, 0);
  const diff = Math.round((100 - sum) * 10000) / 10000;
  pct4[n - 1] = Math.max(0, Math.round((pct4[n - 1] + diff) * 10000) / 10000);
  return pct4;
}

function percentsToAmounts(pcts, total) {
  const T = Math.max(0, Math.round(Number(total || 0)));
  const n = pcts.length || 1;
  if (n === 1) return [T];

  const raw = pcts.map((p) => Math.floor(T * (Number(p) || 0) / 100));
  const sum = raw.reduce((s, x) => s + x, 0);
  raw[n - 1] = Math.max(0, raw[n - 1] + (T - sum)); // drift fix
  return raw;
}

  function statusBadgeClass(status) {
    const s = normalizeInvoiceStatus(status);
    if (s === "paid") return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (s === "sent") return "bg-blue-100 text-blue-700 border-blue-200";
    if (s === "issued") return "bg-cyan-100 text-cyan-700 border-cyan-200";
    if (s === "overdue") return "bg-orange-100 text-orange-700 border-orange-200";
    if (s === "void" || s === "cancelled") return "bg-red-100 text-red-700 border-red-200";
    return "bg-gray-100 text-gray-700 border-gray-200";
  }

  function prettyStatusLabel(status) {
    const s = normalizeInvoiceStatus(status);
    return s === "partially_paid" ? "partially paid" : s;
  }

  const generateLineId = () => `line_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  function computeTotals({ items, ppn, pph, discount }) {
    const subtotal = (items || []).reduce((sum, it) => sum + (Number(it.amount) || 0), 0);

    const discPct = clampPercent(discount);
    const ppnPct = clampPercent(ppn);
    const pphPct = clampPercent(pph);

    const total_discount = (subtotal * discPct) / 100;
    const afterDiscount = Math.max(0, subtotal - total_discount);

    const total_ppn = (afterDiscount * ppnPct) / 100;
    const total_pph = (afterDiscount * pphPct) / 100;

    const grand_total = Math.max(0, afterDiscount + total_ppn - total_pph);
    return { subtotal, total_discount, total_ppn, total_pph, grand_total };
  }

  function toItemState(items) {
    const src = Array.isArray(items) ? items : [];
    if (!src.length) return [{ id: generateLineId(), name: "", description: "", qty: "", price: "", amount: 0 }];
    return src.map((it) => {
      const qty = it?.qty ?? "";
      const price = it?.price ?? "";
      const qtyNum = Math.max(1, Number(qty) || 1);
      const priceNum = Math.max(0, Number(price) || 0);
      return {
        id: generateLineId(),
        name: String(it?.name || ""),
        description: String(it?.description || ""),
        qty,
        price,
        amount: Math.max(0, qtyNum * priceNum),
      };
    });
  }

  /* =========================================================
  * Restructure rules
  * - Excluded: void/cancelled (not in plan)
  * - Locked: paid
  * - Editable: all unpaid (draft/issued/sent/overdue)
  * ========================================================= */
  const EXCLUDED = new Set(["void", "cancelled"]);
  const LOCKED = new Set(["paid", "paid overdue"]);


  function isExcluded(st) {
    return EXCLUDED.has(normalizeInvoiceStatus(st));
  }
  function isLocked(st) {
    return LOCKED.has(normalizeInvoiceStatus(st));
  }
  function isEditableUnpaid(st) {
    const s = normalizeInvoiceStatus(st);
    return !isExcluded(s) && !isLocked(s);
  }

  /* =========================================================
  * Confirmation Modal
  * ========================================================= */
  function ConfirmTextModal({ open, onClose, onConfirm, submitting, requiredText = "Saya yakin ini benar" }) {
    const [text, setText] = useState("");

    useEffect(() => {
      if (!open) return;
      setText("");
    }, [open]);

    if (!open) return null;

    const normalize = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\s+/g, ""); // remove ALL whitespace


    const ok = normalize(text) === normalize(requiredText);

    return createPortal(
      
      <div
      
        className="fixed inset-0 z-[10002] flex items-center justify-center p-4"
        
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900">Konfirmasi Restructure</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Untuk melanjutkan, ketik: <span className="font-semibold text-gray-900">"{requiredText}"</span>
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="flex-shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors disabled:opacity-50"
                title="Tutup"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={submitting}
              placeholder={`Ketik: "${requiredText}"`}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow"
            />

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Batal
              </button>

              <button
                type="button"
                onClick={() => onConfirm?.()}
                disabled={!ok || submitting}
                className="flex-1 px-4 py-2.5 rounded-lg bg-orange-600 text-sm font-medium text-white hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Memproses..." : "Ya, Ubah Plan"}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  /* =========================================================
  * Fetchers
  * ========================================================= */
  async function fetchSaleById(saleId) {
    return await fetchJson(`${BASE_URL}/api/invoice-tracker/sales/${saleId}`);
  }

  async function fetchPreviewInvoiceNos(saleId, top) {
  const qs = new URLSearchParams();
  if (top) qs.set("term_of_payment", String(top));
  return await fetchJson(`${BASE_URL}/api/invoice-tracker/sales/${saleId}/invoices/preview-nos?${qs}`);
}

  async function fetchInvoicesBySaleId(saleId) {
    const qs = new URLSearchParams();
    qs.set("sales_id", saleId);
    return await fetchJson(`${BASE_URL}/api/invoice-tracker/invoices?${qs.toString()}`);
  }
function addMonthsISO(iso, months = 1) {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return new Date().toISOString();

  const day = d.getDate();
  d.setMonth(d.getMonth() + months);

  // optional safety: if month jump overflowed (e.g. Jan 31 -> Mar 3), pull back to last day of prev month
  if (d.getDate() !== day) d.setDate(0);

  return d.toISOString();
}

  /* =========================================================
  * Main Component
  * =========================================================
  *
  * Key change vs your previous version:
  * - "term_of_payment" (TOP) is treated as TOTAL active installments (excluding void/cancelled).
  * - The dropdown controls how many UNPAID rows you want.
  * - TOP is derived: TOP = paidCount + unpaidTargetCount.
  * - planLength always follows TOP so adding "virtual" rows won't get ignored.
  */
  export default function PaymentRestructureForm({ isOpen, onClose, saleId, onSubmit }) {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // keep your UI usage (no-op unless you really implement syncing)
    const [syncingCount] = useState(false);
    const [previewNoByIndex, setPreviewNoByIndex] = useState(new Map());

    const [sale, setSale] = useState(null);
    const [invoices, setInvoices] = useState([]);

    // TOP value (term_of_payment) = TOTAL plan length (excluding void/cancelled)
    const [topValue, setTopValue] = useState(0);

    const [mode, setMode] = useState("plan");

    // For percentage mode: array of % of remaining (must sum to 100)
    const [editablePercentages, setEditablePercentages] = useState([]);

    // For amount mode: { [rowId]: number } (rowId can be real invoice _id or virtual_x)
    const [editableAmounts, setEditableAmounts] = useState({});

    // Due dates for ALL unpaid rows (real + virtual)
    const [editableDueDates, setEditableDueDates] = useState({});

    const [draftUnpaid, setDraftUnpaid] = useState([]);
    const [targetCount, setTargetCount] = useState(1); // unpaid target count

    const [confirmOpen, setConfirmOpen] = useState(false);
    const bodyRef = useRef(null);

    // Sale editable fields (items + taxes)
    const [saleEdit, setSaleEdit] = useState({
      ppn: 11,
      pph: 0,
      discount: 0,
      items: [{ id: generateLineId(), name: "", description: "", qty: "", price: "", amount: 0 }],
    });

    const modeNorm = normalizeMode(mode);

    const reloadAll = async () => {
      if (!saleId) return;
      const [s, inv] = await Promise.all([fetchSaleById(saleId), fetchInvoicesBySaleId(saleId)]);
      setSale(s);
      setInvoices(Array.isArray(inv) ? inv : []);
      return { s, inv };
    };
    const resetAllStateAndReload = async () => {
  if (!saleId) return;

  setLoading(true);

  // hard reset UI states (like "fresh open")
  setMode("plan");
  setEditablePercentages([]);
  setEditableAmounts({});
  setEditableDueDates({});
  setConfirmOpen(false);

  setPreviewNoByIndex(new Map());
  setDraftUnpaid([]);

  try {
    // reload sale + invoices (source of truth)
    const { s, inv } = await reloadAll();

    const list = Array.isArray(inv) ? inv : [];
    const active = list.filter((x) => !isExcluded(x?.status));
    const paidC = active.filter((x) => normalizeInvoiceStatus(x?.status) === "paid").length;

    const topFromSale = Number(s?.term_of_payment ?? 0) || 0;
    const initialTop = Math.max(1, topFromSale || active.length || 1);
    setTopValue(initialTop);

    const initialTarget = Math.max(1, initialTop - paidC);
    setTargetCount(initialTarget);

    // reset sale edit fields to DB values (like fresh open)
    setSaleEdit({
      ppn: Number(s?.ppn ?? 11),
      pph: Number(s?.pph ?? 0),
      discount: Number(s?.discount ?? 0),
      items: toItemState(s?.items),
    });

    // rebuild due map ONLY from current unpaid editable invoices (no virtual)
    const unpaidList = active.filter((x) => isEditableUnpaid(x?.status));
    const dueMap = {};
    for (const x of unpaidList) {
      if (!x?._id) continue;
      dueMap[String(x._id)] = x?.dueDate ? new Date(x.dueDate).toISOString() : "";
    }
    setEditableDueDates(dueMap);

    // make sure scroll is back to top like "fresh open"
    requestAnimationFrame(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    });
  } catch (err) {
    console.error(err);
    toast.error(err?.message || "Gagal reset & reload");
  } finally {
    setLoading(false);
  }
};

    /* ------------------------------
    * Reset when opening
    * ------------------------------ */
    useEffect(() => {
      if (!isOpen) return;

      setMode("plan");
      setEditablePercentages([]);
      setEditableAmounts({});
      setEditableDueDates({});
      setConfirmOpen(false);

      setSale(null);
      setInvoices([]);

      setTopValue(0);
      setTargetCount(1);
      setDraftUnpaid([]);

      setSaleEdit({
        ppn: 11,
        pph: 0,
        discount: 0,
        items: [{ id: generateLineId(), name: "", description: "", qty: "", price: "", amount: 0 }],
      });

      requestAnimationFrame(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
      });
    }, [isOpen]);

    /* ------------------------------
    * Lock body scroll
    * ------------------------------ */
    useEffect(() => {
      if (!isOpen) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }, [isOpen]);

    /* ------------------------------
    * Derived lists
    * ------------------------------ */
    const sortedInvoices = useMemo(() => {
      const list = Array.isArray(invoices) ? invoices : [];
      return list.slice().sort((a, b) => Number(a?.invoice_index || 0) - Number(b?.invoice_index || 0));
    }, [invoices]);

    // Active = not void/cancelled (these are excluded from plan length)
    const activeInvoices = useMemo(() => sortedInvoices.filter((x) => !isExcluded(x?.status)), [sortedInvoices]);

    // slotMap based on current active invoices order (1..activeInvoices.length)
    const slotMap = useMemo(() => {
      const m = new Map();
      activeInvoices.forEach((inv, idx) => m.set(String(inv._id), idx + 1));
      return m;
    }, [activeInvoices]);

    const paidInvoices = useMemo(() => {
      return activeInvoices
        .filter((inv) => normalizeInvoiceStatus(inv?.status) === "paid")
        .map((inv) => ({
          _id: String(inv?._id || ""),
          invoice_index: Number(inv?.invoice_index || 0) || 0,
          slot: slotMap.get(String(inv?._id || "")) || null,
          invoiceNo: inv?.invoiceNo || "-",
          status: normalizeInvoiceStatus(inv?.status),
          total: clampNonNeg(inv?.totals?.grandTotal ?? 0),
          dueDate: inv?.dueDate,
          invoiceDate: inv?.invoiceDate,
        }))
        .filter((x) => x._id);
    }, [activeInvoices, slotMap]);

    const paidCount = useMemo(() => paidInvoices.length, [paidInvoices]);

    // planLength follows TOP (term_of_payment). If TOP not set yet, fallback to current active length.
    const planLength = useMemo(() => {
      const top = Math.max(0, Number(topValue || 0) || 0);
      if (top > 0) return Math.max(1, top);
      return Math.max(1, activeInvoices.length || 1);
    }, [topValue, activeInvoices.length]);

    const unpaidEditableInvoices = useMemo(() => {
      // editable unpaid = not excluded + not paid
      // use activeInvoices to keep slotMap consistent
      return activeInvoices
        .filter((inv) => isEditableUnpaid(inv?.status))
        .map((inv) => ({
          _id: String(inv?._id || ""),
          invoice_index: Number(inv?.invoice_index || 0) || 0,
          slot: slotMap.get(String(inv?._id || "")) || null,
          invoiceNo: inv?.invoiceNo || "-",
          status: normalizeInvoiceStatus(inv?.status),
          total: clampNonNeg(inv?.totals?.grandTotal ?? 0),
          dueDate: inv?.dueDate,
          invoiceDate: inv?.invoiceDate,
          isVirtual: false,
        }))
        .filter((x) => x._id)
        .sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0));
    }, [activeInvoices, slotMap]);
    
    /* ------------ ------------------
    * Sale edit totals (items + ppn/pph/discount)
    * ------------------------------ */
    const saleEditComputedItems = useMemo(() => {
      const src = saleEdit.items || [];
      return src.map((it) => {
        const qtyNum = Math.max(1, Number(it.qty) || 1);
        const priceNum = Math.max(0, Number(it.price) || 0);
        const amount = Math.max(0, qtyNum * priceNum);
        return { ...it, qtyNum, priceNum, amount };
      });
    }, [saleEdit.items]);

    const saleTotals = useMemo(() => {
      const cleanedItems = saleEditComputedItems.map((it) => ({ amount: it.amount }));
      return computeTotals({
        items: cleanedItems,
        ppn: saleEdit.ppn,
        pph: saleEdit.pph,
        discount: saleEdit.discount,
      });
    }, [saleEditComputedItems, saleEdit.ppn, saleEdit.pph, saleEdit.discount]);

    const grandTotal = useMemo(() => clampNonNeg(saleTotals.grand_total), [saleTotals.grand_total]);

    const paidTotal = useMemo(() => {
      return Math.round(paidInvoices.reduce((s, inv) => s + clampNonNeg(inv.total || 0), 0));
    }, [paidInvoices]);

    const remainingTotal = useMemo(() => Math.max(0, Math.round(grandTotal - paidTotal)), [grandTotal, paidTotal]);
 


    /* ------------------------------
    * Existing plan (fallback / padded to planLength)
    * ------------------------------ */
    const existingPlan = useMemo(() => {
      const existing = Array.isArray(sale?.installment_percentages) ? sale.installment_percentages : null;

      // start from existing if any, else equal 100 over current planLength
      let base = existing && existing.length ? existing.map((x) => clampNonNeg(x)) : buildEqualPercentagesForTarget(planLength, 100);

      // pad/truncate to planLength
      if (base.length > planLength) base = base.slice(0, planLength);
      if (base.length < planLength) {
        const add = buildEqualPercentagesForTarget(planLength - base.length, 0); // add zeros
        base = base.concat(add);
      }

      // if sum is 0 (edge), make it equal
      const s = sumNums(base);
      if (Math.round(s) === 0) base = buildEqualPercentagesForTarget(planLength, 100);

      return base;
    }, [sale, planLength]);

    /* ------------------------------
    * % locked & editable target
    * ------------------------------ */
    const lockedPctSumByAmount = useMemo(() => {
      if (!grandTotal) return 0;
      const pct = (paidTotal / grandTotal) * 100;
      return Math.max(0, Math.min(100, Math.round(pct * 10000) / 10000));
    }, [paidTotal, grandTotal]);

    const editableTargetSum = useMemo(() => {
      const left = 100 - lockedPctSumByAmount;
      return Math.max(0, Math.round(left * 10000) / 10000);
    }, [lockedPctSumByAmount]);

    const lockedPlanPctBySlot = useMemo(() => {
      const m = new Map();
      if (!grandTotal) return m;
      for (const p of paidInvoices) {
        const slot = Number(p.slot || 0);
        if (!slot || slot < 1 || slot > planLength) continue;
        const pct = (clampNonNeg(p.total) / grandTotal) * 100;
        m.set(slot, Math.max(0, Math.round(pct * 10000) / 10000));
      }
      return m;
    }, [paidInvoices, grandTotal, planLength]);

    /* ------------------------------
    * Load data on open
    * ------------------------------ */
    useEffect(() => {
      if (!isOpen) return;
        resetAllStateAndReload();

          
      const load = async () => {
        setLoading(true);
        try {
          const { s, inv } = await reloadAll();

       const list = Array.isArray(inv) ? inv : [];

// active invoices count excludes void/cancelled
          const active = list.filter((x) => !isExcluded(x?.status));
          const paidC = active.filter((x) => normalizeInvoiceStatus(x?.status) === "paid").length;

          const topFromSale = Number(s?.term_of_payment ?? 0) || 0;
         const initialTop = Math.max(1, topFromSale || active.length || 1);

         setTopValue(initialTop);

         
          setDraftUnpaid([]);

     // derive target unpaid count from TOP - paid
        const initialTarget = Math.max(1, initialTop - paidC);
        setTargetCount(initialTarget);

        // build unpaid list (editable unpaid)
        const unpaidList = active.filter((x) => isEditableUnpaid(x?.status));


          const dueMap = {};
          for (const x of unpaidList) {
            if (!x?._id) continue;
            dueMap[String(x._id)] = x?.dueDate ? new Date(x.dueDate).toISOString() : "";
          }
          setEditableDueDates(dueMap);

          setSaleEdit({
            ppn: Number(s?.ppn ?? 11),
            pph: Number(s?.pph ?? 0),
            discount: Number(s?.discount ?? 0),
            items: toItemState(s?.items),
          });

          setSale(s);
        } catch (err) {
          console.error(err);
          toast.error(err?.message || "Gagal memuat data untuk restructure");
          setSale(null);
          setInvoices([]);
        } finally {
          setLoading(false);
        }
      };

      load();
    }, [isOpen, saleId]);

    /* ------------------------------
    * Virtual unpaid builder
    * - ensures exactly targetCount rows (existing unpaid + virtual)
    * - virtual slots follow after current max slot
    * ------------------------------ */
    function makeDraftUnpaidRows({ unpaidRows, targetCount, editableDueDates, previewNoByIndex }) {

      const base = Array.isArray(unpaidRows) ? unpaidRows.slice() : [];
      const n = Math.max(1, Math.min(12, Number(targetCount) || 1));

      const sorted = base.slice().sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0));

      const maxInvoiceIndex = sorted.length ? Math.max(...sorted.map((x) => Number(x.invoice_index || 0))) : 0;
      const maxSlotExisting = sorted.length ? Math.max(...sorted.map((x) => Number(x.slot || 0))) : 0;

      while (sorted.length < n) {
        const slot = maxSlotExisting + (sorted.length - base.length) + 1;
        const nextIndex = maxInvoiceIndex + (sorted.length - base.length) + 1;

        sorted.push({
          _id: `virtual_${slot}`,
          isVirtual: true,
          invoice_index: nextIndex,
          slot,
          invoiceNo: previewNoByIndex?.get(nextIndex) || "-",

          status: "draft",
          total: 0,
          dueDate: "",
          invoiceDate: "",
        });
      }

      const trimmed = sorted.slice(0, n);

     // ensure each row has due date default (CHAINED by previous slot)
const bySlot = trimmed.slice().sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0));

for (let i = 0; i < bySlot.length; i++) {
  const r = bySlot[i];
  const k = String(r._id);

  if (editableDueDates[k]) continue;

  // Prefer previous row's due date (works for draft -> virtual crossing)
  const prev = i > 0 ? bySlot[i - 1] : null;
  const prevKey = prev ? String(prev._id) : null;

  const prevISO =
    (prevKey && editableDueDates[prevKey]) ||
    (prev?.dueDate ? new Date(prev.dueDate).toISOString() : "");

  // If there is a previous due date, set this one to +1 month from it.
  // Otherwise fall back to today.
  editableDueDates[k] = prevISO ? addMonthsISO(prevISO, 1) : new Date().toISOString();
}

      return trimmed;
    }

   const effectiveUnpaidRows = useMemo(() => {
  if (draftUnpaid.length) return draftUnpaid;
  // ✅ fallback respects dropdown even when draftUnpaid isn't ready
  return unpaidEditableInvoices
    .slice()
    .sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0))
    .slice(0, targetCount);
}, [draftUnpaid, unpaidEditableInvoices, targetCount]);
  useEffect(() => {
  console.log("[PRF] rows-debug", {
    targetCount,
    paidCount,
    topValue,
    planLength,
    unpaidEditableInvoices_len: unpaidEditableInvoices.length,
    draftUnpaid_len: draftUnpaid.length,
    effectiveUnpaidRows_len: effectiveUnpaidRows.length,
    unpaidSlots: effectiveUnpaidRows.map(r => ({ slot: r.slot, idx: r.invoice_index, id: r._id, v: !!r.isVirtual })),
  });
}, [targetCount, paidCount, topValue, planLength, unpaidEditableInvoices, draftUnpaid, effectiveUnpaidRows]);

       const amountInputSum = useMemo(() => {
  if (modeNorm !== "amount") return 0;
  return effectiveUnpaidRows
    .slice()
    .sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0))
    .reduce((s, u) => s + clampNonNeg(editableAmounts[String(u._id)] ?? 0), 0);
}, [modeNorm, effectiveUnpaidRows, editableAmounts]);

  const amountDelta = useMemo(() => {
  if (modeNorm !== "amount") return 0;
  return Math.round(remainingTotal - amountInputSum); // + = deficit, - = excess
}, [modeNorm, remainingTotal, amountInputSum]);

    /* ------------------------------
    * Editable slots are based on effective rows
    * ------------------------------ */
    const editableIndexes = useMemo(() => {
      const set = new Set(
        effectiveUnpaidRows
          .map((r) => Number(r.slot || 0))
          .filter((x) => Number.isFinite(x) && x >= 1 && x <= planLength)
      );
      return Array.from(set).sort((a, b) => a - b);
    }, [effectiveUnpaidRows, planLength]);

    /* ------------------------------
    * Percentage mode defaults
    * ------------------------------ */
    useEffect(() => {
      if (!isOpen) return;
      if (modeNorm !== "percentage") return;

      const n = editableIndexes.length || 0;
      if (n <= 0) {
        setEditablePercentages([]);
        return;
      }

      setEditablePercentages((prev) => {
        const coerced = coercePercentArray(prev, n);
        const sum = sumNums(coerced);
        if (Math.round(sum) === 100) return coerced;
        return buildEqualPercentagesForTarget(n, 100);
      });
    }, [editableIndexes.length, isOpen, modeNorm]);

    const effectiveEditablePerc = useMemo(() => {
      const n = editableIndexes.length || 0;
      if (n <= 0) return [];
      if (modeNorm === "percentage") return coercePercentArray(editablePercentages, n);
      if (modeNorm === "equal") return buildEqualPercentagesForTarget(n, 100);
      return [];
    }, [editableIndexes.length, editablePercentages, modeNorm]);

    const editablePercSum = useMemo(() => sumNums(effectiveEditablePerc), [effectiveEditablePerc]);

    /* ------------------------------
    * Preview by plan (unpaid distribution)
    * - weights based on existingPlan among unpaid slots
    * - amounts ALWAYS sum to remainingTotal
    * ------------------------------ */
    const previewByPlan = useMemo(() => {
      if (!remainingTotal) return [];

      const perc = existingPlan.slice(0, planLength);
      const unpaidSlots = effectiveUnpaidRows
        .map((u) => Number(u.slot || 0))
        .filter((x) => x >= 1 && x <= planLength);

      const sumUnpaidPlanPct = unpaidSlots.reduce((s, slot) => s + (Number(perc[slot - 1]) || 0), 0);

      const normalizedPctBySlot = new Map();
      if (sumUnpaidPlanPct > 0) {
        for (const slot of unpaidSlots) {
          const p = Number(perc[slot - 1]) || 0;
          normalizedPctBySlot.set(slot, (p / sumUnpaidPlanPct) * 100);
        }
      } else {
        const each = unpaidSlots.length ? 100 / unpaidSlots.length : 0;
        for (const slot of unpaidSlots) normalizedPctBySlot.set(slot, each);
      }

      const raw = unpaidSlots.map((slot) => Math.floor((remainingTotal * (normalizedPctBySlot.get(slot) || 0)) / 100));
      const sumRaw = raw.reduce((a, b) => a + b, 0);
      if (raw.length) raw[raw.length - 1] = Math.max(0, raw[raw.length - 1] + (remainingTotal - sumRaw));

      const slotToAmt = new Map(unpaidSlots.map((slot, i) => [slot, raw[i]]));
      const slotToNormPct = new Map(unpaidSlots.map((slot) => [slot, Number(normalizedPctBySlot.get(slot) || 0)]));

      return effectiveUnpaidRows.map((u) => ({
        ...u,
        amountPreview: Number(slotToAmt.get(Number(u.slot || 0)) || 0),
        pctPreview: Number(slotToNormPct.get(Number(u.slot || 0)) || 0),
      }));
    }, [effectiveUnpaidRows, existingPlan, planLength, remainingTotal]);

    /* ------------------------------
    * Init editableAmounts from plan preview (for ALL effective rows)
    * ------------------------------ */
    useEffect(() => {
      if (!isOpen) return;
      if (!effectiveUnpaidRows.length) {
        setEditableAmounts({});
        return;
      }

      setEditableAmounts((prev) => {
        const allHave = effectiveUnpaidRows.every((u) => prev[String(u._id)] != null);
        if (allHave) return prev;

        const next = { ...prev };
        for (const row of previewByPlan) {
          const id = String(row._id);
          if (next[id] == null) next[id] = clampNonNeg(row.amountPreview || 0);
        }
        return next;
      });
    }, [isOpen, effectiveUnpaidRows, previewByPlan]);

    /* ------------------------------
    * Amount mode => compute plan % for each unpaid slot
    * (plan % is portion of GRAND TOTAL)
    * ------------------------------ */
    const amountModePlanPercBySlot = useMemo(() => {
      if (modeNorm !== "amount") return null;
      if (!remainingTotal) return null;

      const slots = effectiveUnpaidRows
        .map((u) => Number(u.slot || 0))
        .filter((x) => Number.isFinite(x) && x >= 1 && x <= planLength)
        .sort((a, b) => a - b);

      if (!slots.length) return null;

      const amounts = slots.map((slot) => {
        const row = effectiveUnpaidRows.find((u) => Number(u.slot || 0) === slot);
        if (!row) return 0;
        return clampNonNeg(editableAmounts[String(row._id)] ?? 0);
      });

      const sumAmt = amounts.reduce((a, b) => a + b, 0);
      const diff = Math.round(remainingTotal - sumAmt);
      if (amounts.length) amounts[amounts.length - 1] = Math.max(0, Math.round(amounts[amounts.length - 1] + diff));

      const finalSum = amounts.reduce((a, b) => a + b, 0);
      const normPctRemaining = amounts.map((a) => (finalSum > 0 ? (a / finalSum) * 100 : 0));

      const planPct = normPctRemaining.map((p) => (p * editableTargetSum) / 100);

      const planPctRounded = planPct.map((p) => Math.round(p * 10000) / 10000);
      const sumPlan = planPctRounded.reduce((a, b) => a + b, 0);
      const drift = Math.round((editableTargetSum - sumPlan) * 10000) / 10000;
      if (planPctRounded.length) planPctRounded[planPctRounded.length - 1] = Math.max(0, planPctRounded[planPctRounded.length - 1] + drift);

      const map = new Map();
      slots.forEach((slot, i) => map.set(slot, Math.max(0, Math.round(Number(planPctRounded[i] || 0) * 10000) / 10000)));
      return map;
    }, [modeNorm, remainingTotal, effectiveUnpaidRows, planLength, editableAmounts, editableTargetSum]);

    /* ------------------------------
    * New installment_percentages (plan-level, must sum to 100)
    * ------------------------------ */
    const newInstallmentPercentages = useMemo(() => {
      const length = Math.max(1, planLength);
      const out = new Array(length).fill(0);



      // lock paid slots
      for (let i = 1; i <= planLength; i++) {
        if (lockedPlanPctBySlot.has(i)) out[i - 1] = clampNonNeg(lockedPlanPctBySlot.get(i));
      }

      // unpaid slots fill
      const unpaidSlots = effectiveUnpaidRows
        .map((u) => Number(u.slot || 0))
        .filter((x) => x >= 1 && x <= planLength)
        .sort((a, b) => a - b);

      if (modeNorm === "plan") {
        const weights = unpaidSlots.map((slot) => clampNonNeg(existingPlan[slot - 1] ?? 0));
        const wSum = weights.reduce((a, b) => a + b, 0);

        const planPct = unpaidSlots.map((slot, i) => {
          const w = weights[i] || 0;
          const p = wSum > 0 ? (w / wSum) * editableTargetSum : unpaidSlots.length ? editableTargetSum / unpaidSlots.length : 0;
          return Math.round(p * 10000) / 10000;
        });

        const s = planPct.reduce((a, b) => a + b, 0);
        const drift = Math.round((editableTargetSum - s) * 10000) / 10000;
        if (planPct.length) planPct[planPct.length - 1] = Math.max(0, planPct[planPct.length - 1] + drift);

        unpaidSlots.forEach((slot, i) => (out[slot - 1] = clampNonNeg(planPct[i])));
      } else if (modeNorm === "amount" && amountModePlanPercBySlot) {
        unpaidSlots.forEach((slot) => {
          const p = amountModePlanPercBySlot.get(slot);
          if (p != null) out[slot - 1] = clampNonNeg(p);
        });
      } else if (modeNorm === "equal" || modeNorm === "percentage") {
        editableIndexes.forEach((idx, i) => {
          const pRemaining = clampNonNeg(effectiveEditablePerc[i] ?? 0);
          const pPlan = (pRemaining * editableTargetSum) / 100;
          out[idx - 1] = Math.round(pPlan * 10000) / 10000;
        });
      }

      const sum = out.reduce((a, b) => a + (Number(b) || 0), 0);
      const diff = Math.round((100 - sum) * 10000) / 10000;
      if (diff !== 0) {
        const targetSlot = unpaidSlots.length ? unpaidSlots[unpaidSlots.length - 1] : planLength;
        out[targetSlot - 1] = Math.max(0, Math.round((out[targetSlot - 1] + diff) * 10000) / 10000);
      }

      return out.map((x) => Math.max(0, Math.round(Number(x || 0) * 10000) / 10000));
    }, [
      planLength,
      lockedPlanPctBySlot,
      modeNorm,
      effectiveUnpaidRows,
      existingPlan,
      editableTargetSum,
      amountModePlanPercBySlot,
      editableIndexes,
      effectiveEditablePerc,
    ]);

    /* ------------------------------
    * Preview rows for table
    * ------------------------------ */
    const previewUnpaidRows = useMemo(() => {
      if (!effectiveUnpaidRows.length) return [];
      if (!remainingTotal) return effectiveUnpaidRows.map((u) => ({ ...u, amount: 0, pct: 0 }));

      if (modeNorm === "plan") {
        return previewByPlan
          .slice()
          .sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0))
          .map((u) => ({
            ...u,
            amount: clampNonNeg(u.amountPreview || 0),
            pct: clampNonNeg(u.pctPreview || 0),
          }));
      }

      if (modeNorm === "amount") {
        const rows = effectiveUnpaidRows.map((u) => ({
          ...u,
          amount: clampNonNeg(editableAmounts[String(u._id)] ?? 0),
        }));

        const sorted = rows.slice().sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0));


            const finalSum = sorted.reduce((s, r) => s + clampNonNeg(r.amount), 0);
      for (const r of sorted) r.pct = finalSum > 0 ? (clampNonNeg(r.amount) / finalSum) * 100 : 0;
      return sorted;

      }

      // equal/percentage
      const perc = newInstallmentPercentages;
      const unpaidSlots = effectiveUnpaidRows
        .map((u) => Number(u.slot || 0))
        .filter((x) => x >= 1 && x <= planLength)
        .sort((a, b) => a - b);

      const sumUnpaidPlanPct = unpaidSlots.reduce((s, slot) => s + (Number(perc[slot - 1]) || 0), 0);

      const normalizedPctBySlot = new Map();
      if (sumUnpaidPlanPct > 0) {
        for (const slot of unpaidSlots) {
          const p = Number(perc[slot - 1]) || 0;
          normalizedPctBySlot.set(slot, (p / sumUnpaidPlanPct) * 100);
        }
      } else {
        const each = unpaidSlots.length ? 100 / unpaidSlots.length : 0;
        for (const slot of unpaidSlots) normalizedPctBySlot.set(slot, each);
      }

      const raw = unpaidSlots.map((slot) => Math.floor((remainingTotal * (normalizedPctBySlot.get(slot) || 0)) / 100));
      const sumRaw = raw.reduce((a, b) => a + b, 0);
      if (raw.length) raw[raw.length - 1] = Math.max(0, raw[raw.length - 1] + (remainingTotal - sumRaw));

      const slotToAmt = new Map(unpaidSlots.map((slot, i) => [slot, raw[i]]));
      const slotToPct = new Map(unpaidSlots.map((slot) => [slot, Number(normalizedPctBySlot.get(slot) || 0)]));

      return effectiveUnpaidRows
        .slice()
        .sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0))
        .map((u) => ({
          ...u,
          amount: Number(slotToAmt.get(Number(u.slot || 0)) || 0),
          pct: Number(slotToPct.get(Number(u.slot || 0)) || 0),
        }));
    }, [effectiveUnpaidRows, remainingTotal, modeNorm, previewByPlan, editableAmounts, newInstallmentPercentages, planLength]);

    /* ------------------------------
    * UI actions
    * ------------------------------ */
    const resetToFreshOpen = () => {
  if (submitting || syncingCount || loading) return;
  resetAllStateAndReload();
};

    const goEqual = () => {
  setMode("equal");

  const rows = previewUnpaidRows.slice().sort((a,b)=>Number(a.slot||0)-Number(b.slot||0));
  let amts = percentsToAmounts(buildEqualPercentagesForTarget(rows.length || 1, 100), remainingTotal);
amts = enforceMin1Amounts(amts, remainingTotal);


  setEditableAmounts((prevAmt) => {
    const out = { ...prevAmt };
    rows.forEach((r, i) => { out[String(r._id)] = amts[i] ?? 0; });
    return out;
  });

  setEditablePercentages(buildEqualPercentagesForTarget(editableIndexes.length || 1, 100));
};

   const goCustomPercent = () => {
  const rows = previewUnpaidRows.slice().sort((a,b)=>Number(a.slot||0)-Number(b.slot||0));
  const amounts = rows.map((r) => clampNonNeg(editableAmounts[String(r._id)] ?? r.amount ?? 0));

  let pcts = amountsToPercents(amounts, remainingTotal);

  if ((rows.length || 1) > 1) pcts = enforceMin1Percents(pcts);
  else pcts = [100];

  setMode("percentage");
  setEditablePercentages(pcts);
};


   const updateEditablePctAt = (idx, value) => {
  setEditablePercentages((prev) => {
    const n = editableIndexes.length || 1;
    let next = coercePercentArray(prev, n);

    const v = clampPercent(value);
    next[idx] = v;

    // ✅ enforce "each invoice must be filled"
    if (n > 1) {
      next = enforceMin1Percents(next);
    } else {
      next = [100];
    }

    // rebuild amounts
    const rows = previewUnpaidRows.slice().sort((a,b)=>Number(a.slot||0)-Number(b.slot||0));
    let amts = percentsToAmounts(next, remainingTotal);
    amts = enforceMin1Amounts(amts, remainingTotal);

    setEditableAmounts((prevAmt) => {
      const out = { ...prevAmt };
      rows.forEach((r, i) => { out[String(r._id)] = amts[i] ?? 0; });
      return out;
    });

    return next;
  });
};


    const updateAmountAtRow = (rowId, rawValue) => {
  const cleaned = String(rawValue ?? "").replace(/[^\d]/g, "");
  const num = cleaned ? Number(cleaned) : 0;

  const min = remainingTotal > 0 ? 1 : 0; // only force >=1 when there's money to allocate
  const fixed = Math.max(min, clampNonNeg(num));

  setEditableAmounts((prev) => ({ ...prev, [String(rowId)]: fixed }));
};


    const updateDueDateAtRow = (rowId, isoOrNull) => {
      setEditableDueDates((prev) => ({ ...prev, [String(rowId)]: isoOrNull || "" }));
    };

  const applyTargetCount = async (nextCount) => {
  const n = Math.max(1, Math.min(12, Number(nextCount) || 1));
  setTargetCount(n);

  const nextTop = Math.max(1, paidCount + n);
  setTopValue(nextTop);

  if (normalizeMode(mode) === "percentage") setEditablePercentages([]);

  try {
    // ✅ only preview (read-only)
    const prev = await fetchPreviewInvoiceNos(saleId, nextTop);
    const map = new Map((prev?.invoices || []).map((x) => [Number(x.invoice_index), x.invoiceNo]));
    setPreviewNoByIndex(map);

   setDraftUnpaid(() => {
  const dueClone = { ...editableDueDates };

  const rows = makeDraftUnpaidRows({
    unpaidRows: unpaidEditableInvoices
  .slice()
  .sort((a,b) => Number(a.slot||0) - Number(b.slot||0))
  .slice(0, n),

    targetCount: n,
    editableDueDates: dueClone,
    previewNoByIndex: map,
  });

  setEditableDueDates(dueClone);
  console.log("[PRF] draft rows after applyTargetCount", rows.map(r => ({
  id: r._id, slot: r.slot, invoice_index: r.invoice_index, invoiceNo: r.invoiceNo, isVirtual: r.isVirtual
})));

  // ✅ default equal split when count changes (especially when adding)
  const sortedRows = rows.slice().sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0));
  let amts = percentsToAmounts(buildEqualPercentagesForTarget(sortedRows.length || 1, 100), remainingTotal);
  amts = enforceMin1Amounts(amts, remainingTotal);


  setEditableAmounts((prevAmt) => {
    const out = { ...prevAmt };
    sortedRows.forEach((r, i) => {
      out[String(r._id)] = amts[i] ?? 0;
    });
    return out;
  });

  // optional: also move mode to equal to match behavior
  setMode("equal");
  setEditablePercentages(buildEqualPercentagesForTarget(sortedRows.length || 1, 100));
  return rows;
});

  } catch (e) {
    console.error(e);
    toast.error(e?.message || "Gagal preview invoice number");
  }
};

    /* ------------------------------
    * Sale edit handlers
    * ------------------------------ */
    const updateItem = (id, field, value) => {
      setSaleEdit((prev) => ({
        ...prev,
        items: (prev.items || []).map((it) => {
          if (it.id !== id) return it;
          const next = { ...it, [field]: value };
          const qtyNum = Math.max(1, Number(next.qty) || 1);
          const priceNum = Math.max(0, Number(next.price) || 0);
          next.amount = Math.max(0, qtyNum * priceNum);
          return next;
        }),
      }));
    };

    const addItem = () => {
      setSaleEdit((prev) => ({
        ...prev,
        items: [...(prev.items || []), { id: generateLineId(), name: "", description: "", qty: "", price: "", amount: 0 }],
      }));
    };

    const removeItem = (id) => {
      setSaleEdit((prev) => {
        const next = (prev.items || []).filter((it) => it.id !== id);
        return { ...prev, items: next.length ? next : prev.items };
      });
    };

    /* ------------------------------
    * Validation
    * ------------------------------ */
    const validateBeforeConfirm = () => {
      if (!saleId) return false;
      if (loading || submitting || syncingCount) return false;

      // items validation
      const items = saleEdit.items || [];
      const validItems = items.filter((it) => String(it.name || "").trim());
      if (!validItems.length) {
        toast.error("Minimal 1 item dengan nama item.");
        return false;
      }
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (!String(it.name || "").trim()) continue;
        if ((Number(it.qty) || 0) < 1) {
          toast.error(`Item #${i + 1}: qty minimal 1`);
          return false;
        }
        if ((Number(it.price) || 0) < 0) {
          toast.error(`Item #${i + 1}: price minimal 0`);
          return false;
        }
      }

      const ppn = Number(saleEdit.ppn);
      const pph = Number(saleEdit.pph);
      const disc = Number(saleEdit.discount);
      if (Number.isNaN(ppn) || ppn < 0 || ppn > 100) return toast.error("PPN harus 0-100."), false;
      if (Number.isNaN(pph) || pph < 0 || pph > 100) return toast.error("PPH harus 0-100."), false;
      if (Number.isNaN(disc) || disc < 0 || disc > 100) return toast.error("Discount harus 0-100."), false;

      if (grandTotal < paidTotal) {
        toast.error(`Grand Total (${formatCurrency(grandTotal)}) tidak boleh lebih kecil dari total paid (${formatCurrency(paidTotal)}).`);
        return false;
      }

      if (!effectiveUnpaidRows.length) {
        toast.info("Tidak ada invoice unpaid (editable) yang bisa direstructure.");
        return false;
      }

      if (editableTargetSum <= 0) {
        toast.info("Tidak ada porsi yang tersisa untuk invoice unpaid (semua sudah paid).");
        return false;
      }

      if (modeNorm === "percentage" && Math.round(editablePercSum) !== 100) {
        toast.error(`Total persen (unpaid) harus 100% (sekarang ${editablePercSum}%).`);
        return false;
      }

      for (const u of effectiveUnpaidRows) {
        const v = editableDueDates[String(u._id)];
        if (!v) {
          toast.error(`Due date wajib diisi untuk invoice #${u.invoice_index} (${u.status}).`);
          return false;
        }
        const dt = new Date(v);
        if (Number.isNaN(dt.getTime())) {
          toast.error(`Due date invalid untuk invoice #${u.invoice_index}.`);
          return false;
        }
      }

      if (modeNorm === "amount" || modeNorm === "percentage" || modeNorm === "equal") {
  const rows = effectiveUnpaidRows.map((u) => ({
    ...u,
    amount: clampNonNeg(editableAmounts[String(u._id)] ?? 0),
  }));

  const sorted = rows.slice().sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0));
  const sumAmt = sorted.reduce((s, r) => s + clampNonNeg(r.amount), 0);
  const diff = Math.round(remainingTotal - sumAmt);
  if (sorted.length) sorted[sorted.length - 1].amount = Math.max(0, Math.round(sorted[sorted.length - 1].amount + diff));

  const finalSum = sorted.reduce((s, r) => s + clampNonNeg(r.amount), 0);
  for (const r of sorted) r.pct = finalSum > 0 ? (clampNonNeg(r.amount) / finalSum) * 100 : 0;

  return sorted;
}


      return true;
    };

    const handleSubmit = async () => {
      if (!validateBeforeConfirm()) return;
      setConfirmOpen(true);
    };

  const confirmAndSubmit = async () => {
  try {
    setSubmitting(true);

    // ✅ Don't trust topValue (can be stale). Recompute desired TOP now.
    const desiredTop = Math.max(1, Number(paidCount) + Number(targetCount));
    await ensureCountTotal(saleId, desiredTop);


    const invFresh = await fetchInvoicesBySaleId(saleId);
    const invFreshArr = Array.isArray(invFresh) ? invFresh : [];
    setInvoices(invFreshArr);

    // Source of truth: active invoices after ensure-count
    const activeFresh = invFreshArr
      .slice()
      .sort((a, b) => Number(a?.invoice_index || 0) - Number(b?.invoice_index || 0))
      .filter((x) => !isExcluded(x?.status));

    const actualTop = Math.max(1, activeFresh.length);

    // ✅ build cleanedItems FIRST
    const cleanedItems = (saleEdit.items || [])
      .filter((it) => String(it.name || "").trim())
      .map((it) => {
        const qty = Math.max(1, Number(it.qty) || 1);
        const price = Math.max(0, Number(it.price) || 0);
        const amount = Math.max(0, qty * price);
        return {
          name: String(it.name || "").trim(),
          description: String(it.description || "").trim(),
          qty,
          price,
          amount,
        };
      });

    // ✅ compute totals from cleanedItems
    const computed = computeTotals({
      items: cleanedItems.map((x) => ({ amount: x.amount })),
      ppn: saleEdit.ppn,
      pph: saleEdit.pph,
      discount: saleEdit.discount,
    });

  // Decide what we persist
let computedInstallmentPercentages;

if (normalizeMode(mode) === "amount") {
  // Amount mode: persist based on amounts (current behavior)
  computedInstallmentPercentages = buildInstallmentPercentagesFromAmounts({
    grandTotal: computed.grand_total,
    activeInvoices: activeFresh,
    previewUnpaidRows,
  });
} else {
  // Plan / Equal / Percentage: persist user's intended plan
  // IMPORTANT: make sure length matches actualTop after ensure-count
  computedInstallmentPercentages = padOrTrimPlanToLen(newInstallmentPercentages, actualTop);
}

// Build invoice_index -> real invoice _id (after ensure-count, drafts exist)
const idByIndex = new Map();
for (const inv of activeFresh) {
  const idx = Number(inv?.invoice_index || 0);
  if (idx >= 1 && inv?._id) idByIndex.set(idx, String(inv._id));
}

   // slot-based due dates (virtual rows are mapped to real invoiceIds by invoice_index)
const unpaid_due_dates = previewUnpaidRows
  .slice()
  .sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0))
  .map((u) => {
    const idx = Number(u?.invoice_index || 0) || null;

    // Real invoice id:
    // - if row is real already, use its _id
    // - if virtual, resolve it from activeFresh via invoice_index
    const resolvedId =
      (!u?.isVirtual && u?._id ? String(u._id) : null) ||
      (idx ? idByIndex.get(idx) || null : null);

    return {
      invoiceId: resolvedId,              // ✅ never null if ensure-count created it
      slot: Number(u.slot || 0) || null,
      invoice_index: idx,
      dueDate: editableDueDates[String(u._id)] || null, // UI key stays rowId (virtual_*), that's fine
      isVirtual: !!u.isVirtual,
    };
  });
const missing = unpaid_due_dates.filter((x) => !x.invoiceId);
if (missing.length) {
  throw new Error(
    `Ada invoice virtual yang belum punya invoiceId setelah ensure-count. invoice_index: ${missing
      .map((x) => x.invoice_index)
      .join(", ")}`
  );
}

      console.log("[PRF] activeFresh indices", activeFresh.map(x => ({
  id: x._id,
  invoice_index: x.invoice_index,
  status: x.status,
  dueDate: x.dueDate,
  invoiceNo: x.invoiceNo
})));

console.log("[PRF] previewUnpaidRows", previewUnpaidRows.map(r => ({
  id: r._id,
  isVirtual: r.isVirtual,
  slot: r.slot,
  invoice_index: r.invoice_index,
  invoiceNo: r.invoiceNo,
  dueDate: editableDueDates[String(r._id)]
})));

console.log("[PRF] payload unpaid_due_dates", unpaid_due_dates);
console.log("[PRF] term_of_payment actualTop", actualTop);

    await onSubmit?.({
      saleId,

      // sale update
      items: cleanedItems,
      ppn: clampPercent(saleEdit.ppn),
      pph: clampPercent(saleEdit.pph),
      discount: clampPercent(saleEdit.discount),
      subtotal: computed.subtotal,
      total_discount: computed.total_discount,
      total_ppn: computed.total_ppn,
      total_pph: computed.total_pph,
      grand_total: computed.grand_total,

      // restructure
      installment_percentages: computedInstallmentPercentages,
      unpaid_due_dates,

      // TOP (TOTAL plan length)
      term_of_payment: actualTop,
    });

    setConfirmOpen(false);
  } catch (err) {
    console.error(err);
    toast.error(err?.message || "Gagal restructure payment plan");
  } finally {
    setSubmitting(false);
  }
};


    const canRestructure = useMemo(() => {
      if (!saleId) return false;
      if (loading || submitting || syncingCount) return false;
      if (!effectiveUnpaidRows.length) return false;
      if (editableTargetSum <= 0) return false;
      if (grandTotal < paidTotal) return false;

      if (modeNorm === "percentage" && Math.round(editablePercSum) !== 100) return false;

      for (const u of effectiveUnpaidRows) {
        const v = editableDueDates[String(u._id)];
        if (!v) return false;
        const dt = new Date(v);
        if (Number.isNaN(dt.getTime())) return false;
      }

      const items = saleEdit.items || [];
      const validItems = items.filter((it) => String(it.name || "").trim());
      if (!validItems.length) return false;

      return true;
    }, [
      saleId,
      loading,
      submitting,
      syncingCount,
      effectiveUnpaidRows,
      editableTargetSum,
      modeNorm,
      editablePercSum,
      editableDueDates,
      grandTotal,
      paidTotal,
      saleEdit.items,
    ]);

    if (!isOpen) return null;

    /* =========================================================
    * Render
    * ========================================================= */
    return createPortal(
      <div
        className="fixed inset-0 z-[10001] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !confirmOpen) onClose?.();
        }}
      > 
    <style>{`
      /* Remove number input arrows (Chrome, Safari, Edge) */
      input[type=number]::-webkit-outer-spin-button,
      input[type=number]::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      /* Remove number input arrows (Firefox) */
      input[type=number] {
        -moz-appearance: textfield;
        appearance: textfield;
      }
    `}</style>
        <ConfirmTextModal
          open={confirmOpen}
          submitting={submitting}
          onClose={() => {
            if (submitting) return;
            setConfirmOpen(false);
          }}
          onConfirm={confirmAndSubmit}
        />

        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col max-h-[94vh] overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-5">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">Sales Order</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Payment Restructure</h2>
                <p className="text-sm text-gray-600">
                  Sales ID: <span className="font-medium text-gray-900">{sale?.sales_id || saleId || "-"}</span>
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Editable: semua invoice unpaid (Draft / Issued / Sent / Overdue). Amount & due date bisa diubah untuk semua unpaid.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  TOP (term_of_payment): <span className="font-semibold text-gray-900">{topValue || 0}</span>
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors disabled:opacity-50"
                aria-label="Tutup"
                title="Tutup"
                disabled={submitting || syncingCount}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto bg-gray-50 p-6 space-y-6">
            {loading ? (
              <div className="py-20 text-center">
                <div className="inline-block w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-gray-600">Memuat data restructure...</p>
              </div>
            ) : !sale ? (
              <div className="py-20 text-center text-gray-600">Data Sales tidak ditemukan.</div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-5 border border-gray-200">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Grand Total (Edited)</div>
                    <div className="text-xl font-bold text-gray-900">{formatCurrency(grandTotal)}</div>
                    {grandTotal < paidTotal ? (
                      <div className="text-xs text-red-600 mt-2">⚠️ Grand Total tidak boleh lebih kecil dari Paid.</div>
                    ) : null}
                  </div>

                  <div className="bg-white rounded-xl p-5 border border-gray-200">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Sudah Dibayar</div>
                    <div className="text-xl font-bold text-emerald-700">{formatCurrency(paidTotal)}</div>
                    <div className="text-xs text-gray-500 mt-2">{paidInvoices.length ? `${paidInvoices.length} invoice paid` : "Belum ada yang paid"}</div>
                  </div>

                  <div className="bg-white rounded-xl p-5 border border-gray-200">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Sisa Bisa Direstructure</div>
                    <div className="text-xl font-bold text-orange-600">{formatCurrency(remainingTotal)}</div>
                    <div className="text-xs text-gray-500 mt-2">Grand Total (edited) - Paid</div>
                  </div>
                </div>

                {/* Sales Items (editable) */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Sales Items (Editable)</h3>
                      <p className="text-xs text-gray-500 mt-1">User bisa edit qty, price, PPN/PPH/Discount. Grand Total akan berubah.</p>
                    </div>
                    <button
                      type="button"
                      onClick={addItem}
                      disabled={submitting || syncingCount}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-blue-700 border border-blue-200 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-4 h-4" />
                      Tambah Item
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">#</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[220px]">Product</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[320px]">
                            Description
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Qty</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-44">Price</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-44">Amount</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Aksi</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200">
                        {(saleEdit.items || []).map((it, idx) => (
                          <tr key={it.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-600">{idx + 1}</td>

                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={it.name}
                                onChange={(e) => updateItem(it.id, "name", e.target.value)}
                                placeholder="Nama item"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={submitting || syncingCount}
                              />
                            </td>

                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={it.description}
                                onChange={(e) => updateItem(it.id, "description", e.target.value)}
                                placeholder="Deskripsi item"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={submitting || syncingCount}
                              />
                            </td>

                            <td className="px-4 py-3 text-center">
                              <input
                                type="number"
                                min="1"
                                step="1"
                                placeholder="1"
                                value={it.qty === "" ? "" : it.qty}
                                onChange={(e) => updateItem(it.id, "qty", e.target.value)}
                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                                disabled={submitting || syncingCount}
                              />
                            </td>

                            <td className="px-4 py-3 text-center">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0"
                                value={it.price === "" ? "" : it.price}
                                onChange={(e) => updateItem(it.id, "price", e.target.value)}
                                className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                                disabled={submitting || syncingCount}
                              />
                            </td>

                            <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(it.amount)}</td>

                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => removeItem(it.id)}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                                disabled={(saleEdit.items || []).length <= 1 || submitting || syncingCount}
                                title="Hapus item"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>

                      <tfoot className="bg-gray-50 border-t border-gray-200">
                        <tr>
                          <td colSpan={5} className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">
                            Subtotal
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">{formatCurrency(saleTotals.subtotal)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="p-5 border-t border-gray-200 bg-gray-50">
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
    {/* LEFT: PPN/PPH/Discount compact rows */}
    <div className="border rounded-2xl border-gray-200 p-4 bg-white">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">PPN / PPH / Discount</h3>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
            PPN (%) <span className="text-gray-400 font-normal"></span>
          </label>

          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={saleEdit.ppn}
            onChange={(e) => setSaleEdit((p) => ({ ...p, ppn: e.target.value }))}
            className="no-spinner w-44 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={submitting || syncingCount}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">PPH (%)</label>

          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={saleEdit.pph}
            onChange={(e) => setSaleEdit((p) => ({ ...p, pph: e.target.value }))}
            className="no-spinner w-44 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={submitting || syncingCount}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">Discount (%)</label>

          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={saleEdit.discount}
            onChange={(e) => setSaleEdit((p) => ({ ...p, discount: e.target.value }))}
            className="no-spinner w-44 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={submitting || syncingCount}
          />
        </div>
      </div>
    </div>

    {/* RIGHT: Totals with Rp + number aligned like CreateSalesForm */}
    <div className="lg:col-span-2 border rounded-2xl border-gray-200 p-4 bg-gray-50">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">Totals</h3>

      <div className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Total PPN</span>
          <span className="inline-flex items-center gap-3 font-semibold text-gray-900">
            <span className="w-8 text-left">Rp</span>
            <span className="w-24 text-right">
              {new Intl.NumberFormat("id-ID").format(Number(saleTotals.total_ppn || 0))}
            </span>
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-600">Total PPH</span>
          <span className="inline-flex items-center gap-3 font-semibold text-gray-900">
            <span className="w-8 text-left">Rp</span>
            <span className="w-24 text-right">
              {new Intl.NumberFormat("id-ID").format(Number(saleTotals.total_pph || 0))}
            </span>
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-600">Total Discount</span>
          <span className="inline-flex items-center gap-3 font-semibold text-gray-900">
            <span className="w-8 text-left">Rp</span>
            <span className="w-24 text-right">
              {new Intl.NumberFormat("id-ID").format(Number(saleTotals.total_discount || 0))}
            </span>
          </span>
        </div>

        <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
          <span className="text-base font-bold text-gray-900">Grand Total (Edited)</span>
          <span className="text-base font-bold text-blue-600">{formatCurrency(saleTotals.grand_total)}</span>
        </div>
      </div>
    </div>
  </div>
</div>

                </div>

                {/* Paid invoices */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="px-5 py-4 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">Paid Invoices</h3>
                    <p className="text-xs text-gray-500 mt-1">Yang sudah paid (locked).</p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoice No</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Due Date</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {paidInvoices.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                              Belum ada invoice paid.
                            </td>
                          </tr>
                        ) : (
                          paidInvoices.map((r, idx) => (
                            <tr key={r._id} className="hover:bg-gray-50">
                               <td className="px-4 py-3 text-sm font-semibold text-gray-900">{idx + 1}</td>
                               <td className="px-4 py-3 text-sm text-gray-900">{r.invoiceNo}</td>
                              <td className="px-4 py-3 text-center">
                                <span
                                  className={[
                                    "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border",
                                    statusBadgeClass(r.status),
                                  ].join(" ")}
                                >
                                  {prettyStatusLabel(r.status)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">{formatDate(r.dueDate)}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{formatCurrency(r.total)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Unpaid Editable */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Editable Invoices (Unpaid)</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Amount & due date bisa diubah untuk semua invoice unpaid (Draft / Issued / Sent / Overdue).
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">Jumlah unpaid (editable):</span>
                      <select
                        value={targetCount}
                        onChange={(e) => applyTargetCount(e.target.value)}
                        disabled={syncingCount || submitting}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Ubah jumlah invoice unpaid (editable)"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoice No</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Amount (Editable)
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">%</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Due Date (Editable)
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200">
                        {previewUnpaidRows.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                              Tidak ada invoice unpaid (editable).
                            </td>
                          </tr>
                        ) : (
                          previewUnpaidRows.map((r, idx) => (
                            <tr key={r._id} className="hover:bg-gray-50">
                               <td className="px-4 py-3 text-sm font-semibold text-gray-900">{idx + 1}</td>
                               <td className="px-4 py-3 text-sm text-gray-900">{r.invoiceNo}</td>
                              <td className="px-4 py-3 text-center">
                                <span
                                  className={[
                                    "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border",
                                    statusBadgeClass(r.status),
                                  ].join(" ")}
                                >
                                  {prettyStatusLabel(r.status)}
                                </span>
                                {r.isVirtual ? <div className="text-[10px] text-gray-500 mt-1">virtual</div> : null}
                              </td>

                              <td className="px-4 py-3 text-right">
                                {modeNorm === "amount" ? (
                              <input
                                inputMode="numeric"
                                min={remainingTotal > 0 ? 1 : 0}
                                value={(() => {
                                  const k = String(r._id);
                                  const v = editableAmounts[k];
                                  return v == null ? "" : String(v);
                                })()}
                                onChange={(e) => updateAmountAtRow(r._id, e.target.value)}
                                onBlur={() => {
                                  const k = String(r._id);
                                  const v = Number(editableAmounts[k] ?? 0);
                                  if (remainingTotal > 0 && v < 1) {
                                    setEditableAmounts((prev) => ({ ...prev, [k]: 1 }));
                                  }
                                }}
                                disabled={submitting || syncingCount}
                                className="w-40 text-right px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-opacity-50 disabled:cursor-not-allowed"
                                title="Ubah amount (IDR)"
                              />

                              ) : (
                                <span className="text-sm font-semibold text-gray-900">{formatCurrency(r.amount)}</span>
                              )}

                              </td>

                              <td className="px-4 py-3 text-sm text-gray-700 text-center">
  {fmt2(r.pct || 0)}%
</td>


                              <td className="px-4 py-3">
                                <input
                                  type="date"
                                  value={toISODateInput(editableDueDates[String(r._id)] || "")}
                                  onChange={(e) => updateDueDateAtRow(r._id, fromISODateInput(e.target.value))}
                                  disabled={submitting || syncingCount}
                                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>

                      <tfoot className="bg-gray-50 border-t border-gray-200">
                        <tr>
                          <td colSpan={3} className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">
                            Total
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                            {formatCurrency(previewUnpaidRows.reduce((s, r) => s + clampNonNeg(r.amount), 0))}
                          </td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-center">
                        {fmt2(previewUnpaidRows.reduce((s, r) => s + clampNonNeg(r.pct), 0))}%
                      </td>

                          <td className="px-4 py-3 text-xs text-gray-500">Target: {formatCurrency(remainingTotal)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                {modeNorm === "amount" ? (
                      <div className="space-y-2">
                        {amountDelta !== 0 ? (
                          <div className="text-xs bg-red-50 text-red-700 px-3 py-2 rounded-lg border border-red-200">
                            Total input belum sama dengan target{" "}
                            <span className="font-semibold">{formatCurrency(remainingTotal)}</span>.{" "}
                            {amountDelta > 0 ? (
                              <>
                                <span className="font-semibold">Defisit:</span> {formatCurrency(amountDelta)} (kurang).
                              </>
                            ) : (
                              <>
                                <span className="font-semibold">Excess:</span> {formatCurrency(Math.abs(amountDelta))} (kelebihan).
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs bg-emerald-50 text-emerald-700 px-3 py-2 rounded-lg border border-emerald-200">
                            Total input sudah pas: <span className="font-semibold">{formatCurrency(remainingTotal)}</span>.
                          </div>
                        )}

                        <p className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                          Catatan: sistem akan otomatis menyesuaikan <span className="font-semibold">last row</span> saat submit/preview agar total sama.
                        </p>
                      </div>
                    ) : null}

                </div>

                {/* Distribution Controls */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Distribusi Plan (Unpaid)</h3>
                      <p className="text-xs text-gray-500 mt-1">
                      Total plan % untuk unpaid: <span className="font-semibold text-orange-600">{fmt2(editableTargetSum)}%</span> (paid
                  locked: {fmt2(lockedPctSumByAmount)}%)

                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={goEqual}
                        disabled={submitting || syncingCount || editableIndexes.length === 0}
                        className={[
                          "px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                          modeNorm === "equal"
                            ? "bg-blue-600 text-white shadow-sm"
                            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50",
                        ].join(" ")}
                      >
                        Equal
                      </button>

                      <button
                        type="button"
                        onClick={goCustomPercent}
                        disabled={submitting || syncingCount || editableIndexes.length === 0}
                        className={[
                          "px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                          modeNorm === "percentage"
                            ? "bg-blue-600 text-white shadow-sm"
                            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50",
                        ].join(" ")}
                      >
                        Custom %
                      </button>

                      <button
                        type="button"
                        onClick={() => setMode("amount")}
                        disabled={submitting || syncingCount || editableIndexes.length === 0}
                        className={[
                          "px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                          modeNorm === "amount"
                            ? "bg-blue-600 text-white shadow-sm"
                            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50",
                        ].join(" ")}
                      >
                        Custom Amount
                      </button>

                     <button
                      type="button"
                      onClick={resetToFreshOpen}
                      disabled={submitting || syncingCount || editableIndexes.length === 0 || loading}
                      className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Reset semuanya seperti baru buka modal"
                    >
                      Reset
                    </button>

                    </div>
                  </div>

                  {editableIndexes.length > 0 && modeNorm === "percentage" && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {editableIndexes.map((slot, idx) => (
                          <div key={`${slot}-${idx}`} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <label className="block text-xs font-medium text-gray-700 mb-2 text-center">Slot #{slot}</label>
                            <div className="relative">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                value={Number(effectiveEditablePerc[idx] ?? 0)}
                                onChange={(e) => updateEditablePctAt(idx, e.target.value)}
                                className="w-full px-3 py-2 pr-8 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={submitting || syncingCount}
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                                %
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div
                        className={[
                          "flex items-center justify-between px-4 py-3 rounded-lg border-2",
                          Math.round(editablePercSum) === 100 ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300",
                        ].join(" ")}
                      >
                        <span className="text-sm font-medium text-gray-700">Total Unpaid (dari remaining)</span>
                        <span
                          className={[
                            "text-sm font-bold",
                            Math.round(editablePercSum) === 100 ? "text-emerald-700" : "text-red-600",
                          ].join(" ")}
                        >
                          {editablePercSum}% / 100%
                        </span>
                      </div>
                    </div>
                  )}

                  {modeNorm === "amount" && (
                    <p className="text-xs text-gray-500 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                      Mode <span className="font-semibold">Custom Amount</span>: Anda edit amount per invoice unpaid. Sistem akan mengubah{" "}
                      <span className="font-semibold">installment_percentages</span> otomatis berdasarkan proporsi amount terhadap{" "}
                      <span className="font-semibold">{formatCurrency(remainingTotal)}</span>.
                    </p>
                  )}

                  {modeNorm === "plan" && (
                    <p className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                      Mode <span className="font-semibold">Default (Existing Plan)</span>: bobot unpaid mengikuti installment_percentages yang
                      ada, tapi persentase paid/unpaid dihitung dari nominal aktual.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSubmit}
                className="px-6 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canRestructure}
                title={!effectiveUnpaidRows.length ? "Tidak ada invoice unpaid" : "Restructure payment plan"}
              >
                {submitting ? "Memproses..." : syncingCount ? "Syncing..." : "Restructure Plan"}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }
